import { useState } from 'react';
import Input from '../common/Input';
import Button from '../common/Button';
import EmailTemplateEditor from './EmailTemplateEditor';
import { settingsApi } from '../../api/settings';
import { useUIStore } from '../../stores/uiStore';

/**
 * Email Template Settings Component
 * Form for customizing email templates and styling
 */
export default function EmailTemplateSettings({ settings, onChange, allSettings }) {
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [useHtmlTemplates, setUseHtmlTemplates] = useState(true);
  const [isSavingToDb, setIsSavingToDb] = useState(false);
  const { showToast } = useUIStore();

  const handleChange = (section, field, value) => {
    const updated = { ...settings };
    if (!updated[section]) updated[section] = {};
    updated[section][field] = value;
    onChange(updated);
  };

  const handleOpenEditor = (templateType) => {
    setEditingTemplate(templateType);
  };

  const handleSaveTemplate = async (templateType, data) => {
    // Update local state
    const updated = { ...settings };
    if (!updated[templateType]) updated[templateType] = {};
    updated[templateType].html = data.html;
    updated[templateType].design = data.design;
    onChange(updated);
    
    // Automatically save to database
    setIsSavingToDb(true);
    try {
      // Build the full settings object with only email_templates
      const settingsToSave = {
        email_templates: {
          ...settings,
          [templateType]: {
            ...settings[templateType],
            html: data.html,
            design: data.design
          }
        }
      };
      
      await settingsApi.update(settingsToSave, false);
      showToast(`Template "${templateType}" saved to database`, 'success');
    } catch (error) {
      console.error('Failed to save template to database:', error);
      showToast('Failed to save template to database. Click "Save Changes" to try again.', 'error');
    } finally {
      setIsSavingToDb(false);
      setEditingTemplate(null);
    }
  };

  const handleCancelEditor = () => {
    setEditingTemplate(null);
  };

  // If editing a template, show only the editor
  if (editingTemplate) {
    const initialDesign = settings[editingTemplate]?.design;
    const initialHtml = settings[editingTemplate]?.html;
    
    return (
      <EmailTemplateEditor
        templateType={editingTemplate}
        initialDesign={initialDesign}
        initialHtml={initialHtml}
        onSave={(data) => handleSaveTemplate(editingTemplate, data)}
        onCancel={handleCancelEditor}
        isSavingToDb={isSavingToDb}
      />
    );
  }

  return (
    <div className="space-y-8">
      {/* Template Mode Toggle */}
      <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Email Template Format</h4>
            <p className="text-sm text-gray-500 mt-1">
              Choose between HTML visual editor or text-based templates
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setUseHtmlTemplates(false)}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                !useHtmlTemplates
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300'
              }`}
            >
              Text Mode
            </button>
            <button
              onClick={() => setUseHtmlTemplates(true)}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                useHtmlTemplates
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300'
              }`}
            >
              HTML Editor
            </button>
          </div>
        </div>
      </div>

      {useHtmlTemplates ? (
        /* HTML Template Editor Mode */
        <div className="space-y-6">
          {/* Signing Link Email Template */}
          <div className="border border-gray-200 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Contract Signing Email</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Professional HTML email template for contract signing invitations
                </p>
              </div>
              <Button onClick={() => handleOpenEditor('signing_link')}>
                Edit Template
              </Button>
            </div>
            <div className="text-sm text-gray-500">
              Available variables: <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{trainer_name}}'}</code>,{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{contract_title}}'}</code>,{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{signing_url}}'}</code>,{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{expiry_date}}'}</code>
            </div>
          </div>

          {/* Reminder Email Template */}
          <div className="border border-gray-200 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Reminder Email</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Professional HTML email template for contract signing reminders
                </p>
              </div>
              <Button onClick={() => handleOpenEditor('reminder')}>
                Edit Template
              </Button>
            </div>
            <div className="text-sm text-gray-500">
              Available variables: <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{trainer_name}}'}</code>,{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{contract_title}}'}</code>,{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{signing_url}}'}</code>,{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{expiry_date}}'}</code>
            </div>
          </div>

          {/* Contract Signed Confirmation Email Template */}
          <div className="border border-gray-200 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Contract Signed Confirmation</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Professional HTML email template sent to trainers after they sign a contract
                </p>
              </div>
              <Button onClick={() => handleOpenEditor('contract_signed')}>
                Edit Template
              </Button>
            </div>
            <div className="text-sm text-gray-500">
              Available variables: <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{trainer_name}}'}</code>,{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{contract_title}}'}</code>,{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{signed_at}}'}</code>,{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{company_name}}'}</code>
            </div>
          </div>

          {/* Creator Notification Email Template */}
          <div className="border border-gray-200 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Creator Notification</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Professional HTML email template sent to contract creators when a contract is signed
                </p>
              </div>
              <Button onClick={() => handleOpenEditor('creator_notification')}>
                Edit Template
              </Button>
            </div>
            <div className="text-sm text-gray-500">
              Available variables: <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{trainer_name}}'}</code>,{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{trainer_email}}'}</code>,{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{contract_title}}'}</code>,{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{signed_at}}'}</code>,{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{contract_url}}'}</code>
            </div>
          </div>

          {/* Email Styling */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Email Styling</h3>
            <p className="text-sm text-gray-500 mb-4">
              Customize the colors used in email templates.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Header Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={settings.styling?.header_color || '#4a5568'}
                    onChange={(e) => handleChange('styling', 'header_color', e.target.value)}
                    className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.styling?.header_color || '#4a5568'}
                    onChange={(e) => handleChange('styling', 'header_color', e.target.value)}
                    placeholder="#4a5568"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Button Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={settings.styling?.button_color || '#3182ce'}
                    onChange={(e) => handleChange('styling', 'button_color', e.target.value)}
                    className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.styling?.button_color || '#3182ce'}
                    onChange={(e) => handleChange('styling', 'button_color', e.target.value)}
                    placeholder="#3182ce"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Text-Based Template Mode (Legacy) */
        <div className="space-y-8">
          {/* Signing Link Email Template */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Contract Signing Email</h3>
            <p className="text-sm text-gray-500 mb-4">
              Customize the email sent when a contract is ready for signature.
              Available variables: {'{trainer_name}'}, {'{contract_title}'}, {'{signing_url}'}, {'{expiry_date}'}
            </p>
            <div className="space-y-4">
              <Input
                label="Email Subject"
                value={settings.signing_link?.subject || ''}
                onChange={(e) => handleChange('signing_link', 'subject', e.target.value)}
                placeholder="Contract Ready for Signature: {contract_title}"
              />
              <Input
                label="Greeting"
                value={settings.signing_link?.greeting || ''}
                onChange={(e) => handleChange('signing_link', 'greeting', e.target.value)}
                placeholder="Hello {trainer_name},"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Introduction Text
                </label>
                <textarea
                  value={settings.signing_link?.body_intro || ''}
                  onChange={(e) => handleChange('signing_link', 'body_intro', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Your contract is ready for review and signature."
                />
              </div>
              <Input
                label="Button Text"
                value={settings.signing_link?.button_text || ''}
                onChange={(e) => handleChange('signing_link', 'button_text', e.target.value)}
                placeholder="Review & Sign Contract"
              />
              <Input
                label="Footer Text"
                value={settings.signing_link?.footer_text || ''}
                onChange={(e) => handleChange('signing_link', 'footer_text', e.target.value)}
                placeholder="This is an automated message. Please do not reply to this email."
              />
            </div>
          </div>

          {/* Reminder Email Template */}
          <div className="border-t pt-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Reminder Email</h3>
            <p className="text-sm text-gray-500 mb-4">
              Customize the reminder email sent for unsigned contracts.
            </p>
            <div className="space-y-4">
              <Input
                label="Email Subject"
                value={settings.reminder?.subject || ''}
                onChange={(e) => handleChange('reminder', 'subject', e.target.value)}
                placeholder="Reminder: Contract Pending Signature - {contract_title}"
              />
              <Input
                label="Greeting"
                value={settings.reminder?.greeting || ''}
                onChange={(e) => handleChange('reminder', 'greeting', e.target.value)}
                placeholder="Hello {trainer_name},"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Introduction Text
                </label>
                <textarea
                  value={settings.reminder?.body_intro || ''}
                  onChange={(e) => handleChange('reminder', 'body_intro', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="This is a friendly reminder that your contract is still pending signature."
                />
              </div>
              <Input
                label="Button Text"
                value={settings.reminder?.button_text || ''}
                onChange={(e) => handleChange('reminder', 'button_text', e.target.value)}
                placeholder="Sign Contract Now"
              />
              <Input
                label="Footer Text"
                value={settings.reminder?.footer_text || ''}
                onChange={(e) => handleChange('reminder', 'footer_text', e.target.value)}
                placeholder="This is an automated reminder. Please do not reply to this email."
              />
            </div>
          </div>

          {/* Email Styling */}
          <div className="border-t pt-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Email Styling</h3>
            <p className="text-sm text-gray-500 mb-4">
              Customize the colors used in email templates.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Header Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={settings.styling?.header_color || '#4a5568'}
                    onChange={(e) => handleChange('styling', 'header_color', e.target.value)}
                    className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.styling?.header_color || '#4a5568'}
                    onChange={(e) => handleChange('styling', 'header_color', e.target.value)}
                    placeholder="#4a5568"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Button Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={settings.styling?.button_color || '#3182ce'}
                    onChange={(e) => handleChange('styling', 'button_color', e.target.value)}
                    className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.styling?.button_color || '#3182ce'}
                    onChange={(e) => handleChange('styling', 'button_color', e.target.value)}
                    placeholder="#3182ce"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}